/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import {
  CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX,
  COMMAND_INTELLIGENCE_GLOBAL_PREFS_SCOPE_ID,
  LEGACY_COMMAND_INTELLIGENCE_DRAWER_WIDTH_KEY,
  parseCommandIntelligenceDrawerPrefsCacheKey,
} from './buildCommandIntelligenceWidthCacheKey';
import { getCachedItem } from '../../util/cache';

const LEGACY_PER_ROUTE_WIDTH_SUFFIX = '-command-intelligence-drawer-width';

const isDrawerPrefSuffixKey = key => {
  return (
    key?.endsWith?.('-dock') ||
    key?.endsWith?.('-layout-mode') ||
    key?.endsWith?.('-auto-hide-overlap') ||
    key?.endsWith?.('-open') ||
    /-size-(left|right|top|bottom)$/.test(key ?? '')
  );
};

/**
 * Finds older drawer width keys still present in localStorage for the same entity/user.
 *
 * @param {{ entityRef?: string, userIdentityKey?: string, widthCacheKey?: string }} params
 * @returns {string[]}
 */
export const findStoredLegacyWidthCacheKeys = ({ entityRef, userIdentityKey, widthCacheKey } = {}) => {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  const found = [];
  const entityRoutePrefix = entityRef ? `${entityRef}-` : '';
  const scopedPrefix =
    entityRef && userIdentityKey ? `${CDS_COMMAND_INTELLIGENCE_DRAWER_KEY_PREFIX}${entityRef}:${userIdentityKey}:` : '';

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);

    if (!key || key === widthCacheKey || found.includes(key)) {
      continue;
    }

    if (entityRoutePrefix && key.startsWith(entityRoutePrefix) && key.endsWith(LEGACY_PER_ROUTE_WIDTH_SUFFIX)) {
      found.push(key);
      continue;
    }

    if (scopedPrefix && key.startsWith(scopedPrefix) && !isDrawerPrefSuffixKey(key)) {
      const scopeSegment = key.slice(scopedPrefix.length);

      if (scopeSegment && scopeSegment !== COMMAND_INTELLIGENCE_GLOBAL_PREFS_SCOPE_ID) {
        found.push(key);
      }
    }
  }

  return found.sort((left, right) => right.length - left.length);
};

/**
 * Reads a cached drawer preference, falling back to the legacy global key when needed.
 *
 * @param {string} cacheKey
 * @param {string[]} [legacyCacheKeys]
 * @returns {string | null}
 */
export const readCachedDrawerPreference = (cacheKey, legacyCacheKeys = []) => {
  const primary = getCachedItem(cacheKey);

  if (primary !== null) {
    return primary;
  }

  for (const legacyKey of legacyCacheKeys) {
    const legacyValue = getCachedItem(legacyKey);

    if (legacyValue !== null) {
      return legacyValue;
    }
  }

  return null;
};

/**
 * @param {string} widthCacheKey
 * @returns {string[]}
 */
export const getLegacyDrawerPreferenceKeys = widthCacheKey => {
  if (widthCacheKey === LEGACY_COMMAND_INTELLIGENCE_DRAWER_WIDTH_KEY) {
    return [];
  }

  const parsed = parseCommandIntelligenceDrawerPrefsCacheKey(widthCacheKey);
  const legacyKeys = parsed
    ? findStoredLegacyWidthCacheKeys({
        entityRef: parsed?.entityRef,
        userIdentityKey: parsed?.userIdentityKey,
        widthCacheKey,
      })
    : [];

  legacyKeys.push(LEGACY_COMMAND_INTELLIGENCE_DRAWER_WIDTH_KEY);

  return legacyKeys;
};
